import { Check, ChevronDown, ChevronRight, CircleAlert, Download, TriangleAlert, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router";

import { StatusPill, type StatusPillTone } from "nazare-ui/components";
import { PageSection } from "nazare-ui/components/page-section";
import { VideoPreview } from "nazare-ui/components/preview";
import { cn, useStoredAuth } from "nazare-ui/utils";

import {
  fetchQaTestVideoFile,
  formatCostUsd,
  formatDurationMs,
  formatRelativeToNow,
  formatTokens,
  SessionExpiredError,
  useQaRunDetail,
  useQaRunStream,
  type RunLifecycleStatus,
  type RunTestRowDisplay,
  type TestTreeNode,
  type Verdict,
} from "../../../../qa-automation-api";
import { SkeletonBlock } from "../../../../skeleton-block";
import { useDocumentTitle } from "../../../../use-document-title";
import { useSecondaryHeader } from "../../../../secondary-header-context";
import styles from "./run-detail.module.css";

/** Shimmering placeholder shaped like the real header card + tree/detail
 * split below, shown while `useQaRunDetail` has no cached data yet — replaces
 * a bare "Loading run…" string with something that actually previews the
 * page's real layout (same idea as `../recent-runs.tsx`'s `RankedRowSkeleton`
 * and `../../../../skeleton-row.tsx`'s `<SkeletonRow>`). */
function RunDetailSkeleton() {
  return (
    <>
      <section className={styles.headerCard} aria-hidden="true">
        <div className={styles.headerTop}>
          <SkeletonBlock width={90} height={20} />
          <SkeletonBlock width={140} height={14} />
          <SkeletonBlock width={72} height={22} radius="var(--radius-full)" />
        </div>
        <SkeletonBlock width="42%" height={14} />
        <div className={styles.metaGrid}>
          {Array.from({ length: 7 }, (_, index) => (
            <div key={index} className={styles.metaItem}>
              <SkeletonBlock width={54} height={11} />
              <SkeletonBlock width={index % 3 === 0 ? 150 : 100} height={14} />
            </div>
          ))}
        </div>
      </section>
      <div className={styles.splitRow}>
        <section className={styles.treePane}>
          <div className={styles.treePaneTitle}>
            <SkeletonBlock width={80} height={14} />
          </div>
          <div>
            {Array.from({ length: 7 }, (_, index) => (
              <div key={index} className={styles.skeletonTreeRow}>
                <SkeletonBlock width={`${55 + ((index * 13) % 30)}%`} height={13} />
              </div>
            ))}
          </div>
        </section>
        <div className={styles.detailPane}>
          <div className={styles.emptyCard}>
            <SkeletonBlock width="55%" height={14} />
          </div>
        </div>
      </div>
    </>
  );
}

/** Real error visual for a failed/empty run-detail fetch — an icon badge +
 * heading + message, replacing a bare line of muted text in `.emptyCard`
 * (that plain-text convention is still right for benign empty states like
 * "No logs for this run", but a fetch failure deserves to actually look like
 * an error). */
function RunDetailError({ message }: { message: string }) {
  return (
    <section className={styles.errorCard}>
      <div className={styles.errorIconBadge}>
        <CircleAlert className={styles.errorIcon} aria-hidden="true" />
      </div>
      <h2 className={styles.errorHeading}>Couldn't load this run</h2>
      <p className={styles.errorMessage}>{message}</p>
    </section>
  );
}

const STATUS_LABEL: Record<RunLifecycleStatus, string> = {
  queued: "Queued",
  running: "Running",
  passed: "Complete",
  failed: "Failed",
  abandoned: "Abandoned",
};

const STATUS_TONE: Record<RunLifecycleStatus, StatusPillTone> = {
  queued: "neutral",
  running: "info",
  passed: "success",
  failed: "error",
  abandoned: "neutral",
};

/** Display bucket a full `Verdict` (7 values) rolls up to for glyph/color —
 * matches the reference app's `VERDICT_META` (`verdict-meta.ts`). `null` =
 * pending: the row was snapshotted at enumeration but hasn't finished yet. */
type VerdictBucket = "pass" | "passWithNote" | "fail" | "naScope" | "unknown";

function bucketFor(verdict: Verdict): VerdictBucket {
  switch (verdict) {
    case "pass":
      return "pass";
    case "pass_with_sub_finding":
    case "flag_ux":
      return "passWithNote";
    case "fail_regression":
    case "fail_caught_tweak":
      return "fail";
    case "na_scope":
      return "naScope";
    case "unknown":
      return "unknown";
  }
}

const BUCKET_META: Record<VerdictBucket, { label: string; icon: typeof Check }> = {
  pass: { label: "Pass", icon: Check },
  passWithNote: { label: "Pass w/ comment", icon: TriangleAlert },
  fail: { label: "Fail", icon: X },
  naScope: { label: "N/A", icon: X },
  unknown: { label: "Unknown", icon: X },
};

const VERDICT_CHIP_CLASS: Record<VerdictBucket, string> = {
  pass: styles.verdictChip_pass,
  passWithNote: styles.verdictChip_passWithNote,
  fail: styles.verdictChip_fail,
  naScope: styles.verdictChip_naScope,
  unknown: styles.verdictChip_naScope,
};

const LEAF_ICON_COLOR: Record<VerdictBucket, string> = {
  pass: "var(--green)",
  passWithNote: "var(--orange)",
  fail: "var(--red)",
  naScope: "var(--nzr-muted)",
  unknown: "var(--nzr-muted)",
};

function bucketForNullable(verdict: Verdict | null): VerdictBucket | null {
  return verdict ? bucketFor(verdict) : null;
}

/** Whether `node` or any of its descendants has at least one test matching
 * `filter` — determines whether a group renders at all while filtered, not
 * just whether its own direct tests match. `filter === null` (no filter
 * active) always matches. */
function nodeHasVisibleDescendant(
  node: TestTreeNode,
  testsById: Map<string, RunTestRowDisplay>,
  filter: VerdictBucket | null,
): boolean {
  if (filter === null) return true;
  if (node.testIds.some((id) => bucketForNullable(testsById.get(id)?.verdict ?? null) === filter)) {
    return true;
  }
  return node.children.some((child) => nodeHasVisibleDescendant(child, testsById, filter));
}

/** Whether `node` or any of its descendants is `testId` — used to
 * force-expand the path down to a deep-linked/selected test (loading
 * `/qa-automation/run/:runId/:testId` directly should show that test
 * highlighted in an already-open tree, not leave it buried under
 * collapsed groups the user has to manually find). */
function nodeContainsTest(node: TestTreeNode, testId: string | null): boolean {
  if (testId === null) return false;
  if (node.testIds.includes(testId)) return true;
  return node.children.some((child) => nodeContainsTest(child, testId));
}

function truncateRunId(runId: string): string {
  return runId.length > 18 ? `${runId.slice(0, 18)}…` : runId;
}

function VerdictGlyph({ verdict }: { verdict: Verdict | null }) {
  if (!verdict) return <span className={styles.pendingGlyph}>○</span>;
  const bucket = bucketFor(verdict);
  const Icon = BUCKET_META[bucket].icon;
  return (
    <Icon
      className={styles.treeLeafGlyph}
      style={{ color: LEAF_ICON_COLOR[bucket] }}
      strokeWidth={2.5}
      aria-hidden="true"
    />
  );
}

function GroupSummary({ aggregate }: { aggregate: TestTreeNode["aggregate"] }) {
  return (
    <span className={styles.treeGroupSummary}>
      {aggregate.pass > 0 && (
        <span className={styles.treeGroupSummaryIcon} style={{ color: "var(--green)" }}>
          <Check size={11} strokeWidth={2.5} aria-hidden="true" />
          {aggregate.pass}
        </span>
      )}
      {aggregate.passWithSubFinding > 0 && (
        <span className={styles.treeGroupSummaryIcon} style={{ color: "var(--orange)" }}>
          <TriangleAlert size={11} strokeWidth={2.5} aria-hidden="true" />
          {aggregate.passWithSubFinding}
        </span>
      )}
      {aggregate.failRegression > 0 && (
        <span className={styles.treeGroupSummaryIcon} style={{ color: "var(--red)" }}>
          <X size={11} strokeWidth={2.5} aria-hidden="true" />
          {aggregate.failRegression}
        </span>
      )}
      {aggregate.naScope > 0 && (
        <span className={styles.treeGroupSummaryIcon} style={{ color: "var(--nzr-muted)" }}>
          ◌{aggregate.naScope}
        </span>
      )}
      <span>· {formatDurationMs(aggregate.durationMs)}</span>
      <span>· {formatCostUsd(aggregate.costUsd)}</span>
    </span>
  );
}

function TestLeafRow({
  test,
  depth,
  selected,
  onSelect,
}: {
  test: RunTestRowDisplay;
  depth: number;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    <div className={styles.treeLeaf}>
      <button
        type="button"
        onClick={() => onSelect(test.id)}
        aria-pressed={selected}
        className={cn(styles.treeLeafButton, selected && styles.treeLeafButtonSelected)}
        style={{ paddingLeft: 12 + depth * 16 }}
      >
        <span className={styles.treeLeafGlyphSlot}>
          <VerdictGlyph verdict={test.verdict} />
        </span>
        <span className={styles.treeLeafName} title={test.title}>
          {test.primary}
        </span>
        <span className={styles.treeLeafMeta}>
          <span className={styles.treeLeafDuration}>{formatDurationMs(test.durationMs)}</span>
          <span className={styles.treeLeafCost}>{formatCostUsd(test.costUsd)}</span>
          <span className={styles.treeLeafTokens}>
            {formatTokens(test.tokens?.input)} in · {formatTokens(test.tokens?.output)} out
          </span>
        </span>
      </button>
    </div>
  );
}

function TreeGroupRow({
  node,
  depth,
  testsById,
  selectedTestId,
  onSelect,
  filter,
}: {
  node: TestTreeNode;
  depth: number;
  testsById: Map<string, RunTestRowDisplay>;
  selectedTestId: string | null;
  onSelect: (id: string) => void;
  filter: VerdictBucket | null;
}) {
  const [manuallyExpanded, setManuallyExpanded] = useState(depth < 1);
  // Forces open while a verdict filter is active (a match buried under a
  // collapsed group would otherwise be invisible) or while this group
  // contains the currently selected/deep-linked test (loading
  // `/qa-automation/run/:runId/:testId` directly should show that test
  // already visible, not buried). Reverts to the user's own expand/collapse
  // state once neither condition applies.
  const expanded = filter !== null || manuallyExpanded || nodeContainsTest(node, selectedTestId);

  const visibleChildren = node.children.filter((child) =>
    nodeHasVisibleDescendant(child, testsById, filter),
  );
  const visibleTestIds = node.testIds.filter(
    (id) => filter === null || bucketForNullable(testsById.get(id)?.verdict ?? null) === filter,
  );

  if (filter !== null && visibleChildren.length === 0 && visibleTestIds.length === 0) {
    return null;
  }

  return (
    <div className={styles.treeGroup}>
      <button
        type="button"
        onClick={() => setManuallyExpanded((v) => !v)}
        className={styles.treeGroupButton}
        style={{ paddingLeft: 12 + depth * 16 }}
      >
        {expanded ? (
          <ChevronDown className={styles.treeGroupChevron} aria-hidden="true" />
        ) : (
          <ChevronRight className={styles.treeGroupChevron} aria-hidden="true" />
        )}
        <span className={styles.treeGroupName} title={node.path.join(" › ")}>
          {node.name}
        </span>
        <GroupSummary aggregate={node.aggregate} />
      </button>
      {expanded && (
        <div>
          {visibleChildren.map((child) => (
            <TreeGroupRow
              key={child.path.join("›")}
              node={child}
              depth={depth + 1}
              testsById={testsById}
              selectedTestId={selectedTestId}
              onSelect={onSelect}
              filter={filter}
            />
          ))}
          {visibleTestIds.map((testId) => {
            const test = testsById.get(testId);
            if (!test) return null;
            return (
              <TestLeafRow
                key={test.id}
                test={test}
                depth={depth + 1}
                selected={selectedTestId === test.id}
                onSelect={onSelect}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Fetches the selected test's real recording as an authenticated `File`
 * (a bare `<video src>` can't send the Bearer header), then hands it to
 * `nazare-ui`'s shared `VideoPreview` for actual rendering — the fetch/auth
 * concern lives here, the player chrome lives in one shared place instead
 * of a bespoke one per app. */
function TestVideo({ path, testId }: { path: string; testId: string }) {
  const auth = useStoredAuth();
  const [file, setFile] = useState<File | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFile(null);
    setFailed(false);

    fetchQaTestVideoFile(auth, path, testId)
      .then((f) => {
        if (!cancelled) setFile(f);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [auth, path, testId]);

  if (failed) {
    return <p className={styles.videoErrorText}>Couldn't load the recording for this test.</p>;
  }

  if (!file) {
    return (
      <div className={styles.detailVideoLoading}>
        <p className={styles.emptyState}>Loading recording…</p>
      </div>
    );
  }

  return <VideoPreview file={file} />;
}

function TestDetailPanel({ test, videoPath }: { test: RunTestRowDisplay | null; videoPath: string | null }) {
  if (!test) {
    return (
      <div className={styles.detailEmpty}>
        <p className={styles.emptyState}>Select a test to see its prompt, result and video.</p>
      </div>
    );
  }

  const bucket = test.verdict ? bucketFor(test.verdict) : null;
  const result = test.summary || test.errorMessage;

  return (
    <div className={styles.detailCard}>
      <div>
        {test.context && (
          <p className={styles.detailContext} title={test.context}>
            {test.context}
          </p>
        )}
        <h3 className={styles.detailTitle}>{test.primary}</h3>
        <div className={styles.detailChipsRow}>
          {bucket ? (
            <span className={cn(styles.verdictChip, VERDICT_CHIP_CLASS[bucket])}>
              {(() => {
                const Icon = BUCKET_META[bucket].icon;
                return <Icon size={12} strokeWidth={2.5} aria-hidden="true" />;
              })()}
              <span>{BUCKET_META[bucket].label}</span>
            </span>
          ) : (
            <span className={cn(styles.verdictChip, styles.verdictChip_naScope)}>Pending</span>
          )}
          <span className={styles.detailMeta}>{formatDurationMs(test.durationMs)}</span>
          {test.attempts > 1 && <span className={styles.detailMeta}>· {test.attempts} attempts</span>}
          <span className={cn(styles.detailMeta, styles.detailMetaStrong)}>
            · {formatCostUsd(test.costUsd)}
          </span>
          {test.tokens && (
            <span className={styles.detailMeta}>
              · {formatTokens(test.tokens.input)} in · {formatTokens(test.tokens.output)} out
            </span>
          )}
        </div>
      </div>

      {videoPath && <TestVideo path={videoPath} testId={test.id} />}

      {test.promptText && (
        <div className={styles.detailSection}>
          <p className={styles.detailSectionLabel}>Prompt</p>
          <pre className={styles.detailPre}>{test.promptText}</pre>
        </div>
      )}

      {result && (
        <div className={styles.detailSection}>
          <p className={styles.detailSectionLabel}>{test.summary ? "Result" : "Error"}</p>
          <pre className={styles.detailPre}>{result}</pre>
        </div>
      )}

      {!videoPath && !test.promptText && !result && (
        <p className={styles.emptyState}>No prompt, result or video recorded for this test.</p>
      )}
    </div>
  );
}

/**
 * `/qa-automation/run/:runId` route: the drill-down detail page reached by
 * clicking a row in either QA Automation "Recent runs" table (the dashboard
 * card `../recent-runs.tsx` or the maximised page `./recent-runs.tsx`).
 * Backed by the real BFF (`useQaRunDetail` in `../../../../qa-automation-api`)
 * — the same `/qa-automation/runs/:runId` endpoint and response shape the
 * reference app's own `run-detail.tsx` consumes. Structurally mirrors that
 * reference page for the completed-run case:
 * header meta → verdict tally → action bar (real report/video downloads) →
 * a tests tree (left, server-built and server-aggregated) with a sticky
 * detail panel (right) showing the selected test's real prompt, result, and
 * recording (streamed as an authenticated blob), plus the authenticated SSE
 * feed used for live run updates and screencast frames.
 */
export function QaRunDetailPage() {
  // The selected test is a URL path segment. The verdict is a query-string
  // filter over the same result set.
  const { runId = "" } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: detail, isLoading, error } = useQaRunDetail(runId);
  const live = useQaRunStream(runId, detail?.status === "running" || detail?.status === "queued");

  const pathAfterRunId = location.pathname
    .replace(/^\/qa-automation\/run\/[^/]+\/?/, "")
    .split("/")
    .filter(Boolean);
  const selectedTestId = pathAfterRunId[0] === "results" ? pathAfterRunId[1] ?? null : null;
  const setSelectedTestId = (nextTestId: string | null) => {
    navigate(
      {
        pathname: nextTestId
          ? `/qa-automation/run/${runId}/results/${nextTestId}`
          : `/qa-automation/run/${runId}`,
        // Preserves the verdict filter (a query string) across a test
        // selection within the results page.
        search: location.search,
      },
      { replace: true },
    );
  };

  const [searchParams, setSearchParams] = useSearchParams();

  const verdictFilterParam = searchParams.get("verdict");
  const verdictFilter: VerdictBucket | null =
    verdictFilterParam && verdictFilterParam in BUCKET_META ? (verdictFilterParam as VerdictBucket) : null;
  const setVerdictFilter = (bucket: VerdictBucket | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (bucket) next.set("verdict", bucket);
        else next.delete("verdict");
        return next;
      },
      { replace: true },
    );
  };

  useDocumentTitle(`Run ${truncateRunId(runId)}`);

  const testsById = useMemo(
    () => new Map((detail?.tests ?? []).map((test) => [test.id, test])),
    [detail?.tests],
  );
  const videoPathByTestId = useMemo(
    () => new Map((detail?.presigned.videos ?? []).map((v) => [v.testId, v.url])),
    [detail?.presigned.videos],
  );
  const selectedTest = selectedTestId ? testsById.get(selectedTestId) ?? null : null;

  // Memoized, not a plain `const` — `tabBarRow` below depends on this array
  // and is itself passed to `useSecondaryHeader`, which requires a
  // referentially stable value (see that hook's own doc comment): a fresh
  // array literal every render would make `tabBarRow` a new object every
  // render too, re-firing that hook's effect every render — an infinite
  // render loop, not just a wasted recompute.
  const verdictFilterOptions = useMemo<{ bucket: VerdictBucket; count: number }[]>(
    () =>
      detail
        ? [
            { bucket: "pass" as const, count: detail.counts.pass },
            { bucket: "passWithNote" as const, count: detail.counts.passWithSubFinding },
            { bucket: "fail" as const, count: detail.counts.failRegression },
            { bucket: "naScope" as const, count: detail.counts.naScope },
          ].filter((option) => option.count > 0)
        : [],
    [detail],
  );

  const tabBarRow = useMemo(
    () => (
      <div className={styles.tabBarRow}>
        {verdictFilterOptions.length > 0 && (
          <div className={styles.tabBar} role="group" aria-label="Filter tests by verdict">
            <button
              type="button"
              aria-pressed={verdictFilter === null}
              className={cn(styles.tab, verdictFilter === null && styles.tabActive)}
              onClick={() => setVerdictFilter(null)}
            >
              All
            </button>
            {verdictFilterOptions.map(({ bucket, count }) => {
              const meta = BUCKET_META[bucket];
              const Icon = meta.icon;
              const isActive = verdictFilter === bucket;
              return (
                <button
                  key={bucket}
                  type="button"
                  aria-pressed={isActive}
                  className={cn(styles.tab, styles.verdictFilterTab, isActive && styles.tabActive)}
                  style={{ color: isActive ? LEAF_ICON_COLOR[bucket] : undefined }}
                  onClick={() => setVerdictFilter(isActive ? null : bucket)}
                >
                  <Icon size={12} strokeWidth={2.5} style={{ color: LEAF_ICON_COLOR[bucket] }} aria-hidden="true" />
                  {meta.label}
                  <span className={styles.verdictFilterCount}>{count}</span>
                </button>
              );
            })}
          </div>
        )}
        <span className={styles.spacer} />
        {detail && (
          <div className={styles.actionBar}>
            <a href={detail.presigned.reportZipUrl} download className={styles.actionButtonLink}>
              <Download className={styles.actionIcon} aria-hidden="true" />
              HTML report (.zip)
            </a>
            {detail.presigned.failureVideosZipUrl && (
              <a href={detail.presigned.failureVideosZipUrl} download className={styles.actionButtonLink}>
                <Download className={styles.actionIcon} aria-hidden="true" />
                Failure videos (.zip)
              </a>
            )}
          </div>
        )}
      </div>
    ),
    [detail, verdictFilter, verdictFilterOptions],
  );

  useSecondaryHeader(tabBarRow);

  if (isLoading && !detail) {
    return (
      <div className={styles.page}>
        <RunDetailSkeleton />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className={styles.page}>
        <RunDetailError
          message={
            error instanceof SessionExpiredError
              ? "Your session has expired — sign in to the main app in this browser, then reload."
              : "It may not exist, or the BFF may be unreachable."
          }
        />
      </div>
    );
  }

  const status: RunLifecycleStatus = detail.status ?? (detail.counts.total > 0 ? "passed" : "queued");

  return (
    <div className={styles.page}>
      <section className={styles.headerCard}>
        <div className={styles.headerTop}>
          <h2 className={styles.name}>QA run</h2>
          <span className={styles.secondaryId}>{runId}</span>
          <StatusPill tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</StatusPill>
        </div>
        <p className={styles.description}>Automated QA test run for the {detail.project} project.</p>

        <div className={styles.metaGrid}>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Run ID</span>
            <span className={cn(styles.metaValue, styles.mono)}>{runId}</span>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Environment</span>
            <span className={styles.metaValue}>{detail.environment.label}</span>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>User</span>
            <span className={cn(styles.metaValue, styles.mono)}>{detail.user.email ?? "(no auth)"}</span>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Project</span>
            <span className={styles.metaValue}>{detail.project}</span>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>When</span>
            <span className={styles.metaValue}>{formatRelativeToNow(detail.startedAt)}</span>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Duration</span>
            <span className={styles.metaValue}>{formatDurationMs(detail.durationMs)}</span>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>LLM cost</span>
            <span className={styles.metaValue}>{formatCostUsd(detail.costUsd)}</span>
          </div>
          {detail.ci.branch && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Branch</span>
              <span className={cn(styles.metaValue, styles.mono)}>{detail.ci.branch}</span>
            </div>
          )}
          {detail.ci.sha && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>SHA</span>
              <span className={cn(styles.metaValue, styles.mono)}>{detail.ci.sha.slice(0, 10)}</span>
            </div>
          )}
          {detail.ci.actor && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Actor</span>
              <span className={styles.metaValue}>{detail.ci.actor}</span>
            </div>
          )}
        </div>
      </section>

      <>
          {live.frame && status === "running" && (
            <PageSection
              title={`Live · ${live.frame.project}`}
              description={live.frame.title}
              badges={<span className={styles.liveDot} aria-hidden="true" />}
              padded
            >
              <img
                className={styles.liveFrame}
                src={`data:image/jpeg;base64,${live.frame.data}`}
                alt={`Live preview of ${live.frame.title}`}
              />
            </PageSection>
          )}
          <div className={styles.splitRow}>
            <section className={styles.treePane}>
              <h3 className={styles.treePaneTitle}>
                Tests ({detail.tests.length})
                {verdictFilter && (
                  <span className={styles.treePaneFilterNote}>
                    · filtered to {BUCKET_META[verdictFilter].label}
                  </span>
                )}
              </h3>
              <div>
                {detail.tree.children
                  .filter((child) => nodeHasVisibleDescendant(child, testsById, verdictFilter))
                  .map((child) => (
                    <TreeGroupRow
                      key={child.path.join("›")}
                      node={child}
                      depth={0}
                      testsById={testsById}
                      selectedTestId={selectedTestId}
                      onSelect={setSelectedTestId}
                      filter={verdictFilter}
                    />
                  ))}
                {detail.tree.testIds
                  .filter(
                    (testId) =>
                      verdictFilter === null ||
                      bucketForNullable(testsById.get(testId)?.verdict ?? null) === verdictFilter,
                  )
                  .map((testId) => {
                    const test = testsById.get(testId);
                    if (!test) return null;
                    return (
                      <TestLeafRow
                        key={test.id}
                        test={test}
                        depth={0}
                        selected={selectedTestId === test.id}
                        onSelect={setSelectedTestId}
                      />
                    );
                  })}
              </div>
            </section>
            <div className={styles.detailPane}>
              <TestDetailPanel
                test={selectedTest}
                videoPath={selectedTestId ? videoPathByTestId.get(selectedTestId) ?? null : null}
              />
            </div>
          </div>
      </>
    </div>
  );
}
